// MonthDataService reads static month JSON (Refs: R1,R5,R9,G1,G16)
import { logger } from '../../logging/logger'; // G16 structured logging
import {
  sanitizeMonthExamplePosts,
  type MonthExamplePost,
  type MonthHighlightItem,
} from '../utils/month-highlights';

export interface DaySummary {
  date: string;
  postsCount: number;
  sentiments: { positive: number; neutral: number; negative: number };
  summary: string;
  hasDetail: boolean;
  generatedAt: string;
  topics: string[];
  trendingTopic?: string;
  trendingName?: string;
  selfPraiseCount: number;
  secondaryHighlights?: Array<{ topic: string; name?: string }>;
}

export interface MonthView {
  month: string;
  days: DaySummary[];
  trendingTopic?: string;
  trendingName?: string;
  examplePosts?: MonthExamplePost[];
  secondaryHighlights?: MonthHighlightItem[];
}

export interface MonthDataServiceOptions {
  dataRoot?: string;
  fetchFn?: typeof fetch;
}

function trimTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

function resolveDataRoot(options?: MonthDataServiceOptions): string {
  // G1: Configurable data root via env or runtime global
  const explicit = options?.dataRoot;
  const envUrl = typeof process !== 'undefined' ? process.env?.STATIC_DATA_URL : undefined;
  const globalUrl = typeof globalThis !== 'undefined' ? (globalThis as any).__STATIC_DATA_URL__ : undefined;
  const candidate = explicit || envUrl || globalUrl || '/data';
  return trimTrailingSlash(candidate);
}

function asOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function asHighlightList(value: unknown): MonthHighlightItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return undefined;
      const topic = asOptionalString((item as { topic?: unknown }).topic);
      if (!topic) return undefined;
      const name = asOptionalString((item as { name?: unknown }).name);
      const examplePosts = sanitizeMonthExamplePosts((item as { examplePosts?: unknown }).examplePosts);
      return {
        topic,
        ...(name ? { name } : {}),
        ...(examplePosts.length ? { examplePosts } : {}),
      };
    })
    .filter((item): item is MonthHighlightItem => !!item);
}

export class MonthDataService {
  private readonly fetchFn: typeof fetch;
  private readonly dataRoot: string;
  private readonly cache = new Map<string, MonthView>();

  constructor(options?: MonthDataServiceOptions) {
    this.fetchFn = options?.fetchFn ?? fetch;
    this.dataRoot = resolveDataRoot(options);
  }

  async getMonth(year: number, month: number, options?: { forceRefresh?: boolean }): Promise<MonthView> {
    const monthStr = `${year}-${String(month).padStart(2, '0')}`;
    const cacheKey = monthStr;

    if (!options?.forceRefresh && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!; // R9: simple client cache to avoid duplicate requests
    }

    const url = `${this.dataRoot}/month/${monthStr}.json`; // R1 static path
    const fetchFn = this.fetchFn; // Preserve native fetch realm binding to avoid "Illegal invocation" in browser
    const resp = await fetchFn(url);

    if (resp.status === 404) {
      // Missing month files are empty calendars so prev/next can walk history (R1)
      const empty: MonthView = { month: monthStr, days: [] };
      this.cache.set(cacheKey, empty);
      return empty;
    }

    if (!resp.ok) {
      logger.error('Month data request failed', { status: resp.status, statusText: resp.statusText }); // G16
      throw new Error(`Month data request failed (${resp.status} ${resp.statusText})`);
    }

    const payload = (await resp.json()) as {
      month?: string;
      trendingTopic?: string;
      trendingName?: string;
      examplePosts?: unknown;
      secondaryHighlights?: unknown;
      days: Array<{
        date: string;
        counts: { positive: number; neutral: number; negative: number };
        postsCount: number;
        summary?: string;
        hasDetail?: boolean;
        generatedAt?: string;
        topics?: unknown;
        trendingTopic?: string;
        trendingName?: string;
        selfPraiseCount?: number;
        secondaryHighlights?: unknown;
      }>;
    };
    const mapped: DaySummary[] = (payload?.days || []).map((d) => {
      const secondaryHighlights = asHighlightList(d.secondaryHighlights);
      return {
        date: d.date,
        postsCount: d.postsCount,
        sentiments: d.counts ?? { positive: 0, neutral: 0, negative: 0 },
        summary: d.summary ?? '',
        hasDetail: d.hasDetail ?? true,
        generatedAt: d.generatedAt ?? '',
        topics: Array.isArray(d.topics) ? d.topics.filter((t): t is string => typeof t === 'string') : [],
        trendingTopic: asOptionalString(d.trendingTopic),
        trendingName: asOptionalString(d.trendingName),
        selfPraiseCount: Number.isFinite(Number(d.selfPraiseCount)) ? Math.max(0, Math.trunc(Number(d.selfPraiseCount))) : 0,
        ...(secondaryHighlights.length ? { secondaryHighlights } : {}),
      };
    });
    const monthSecondaries = asHighlightList(payload?.secondaryHighlights);
    const examplePosts = sanitizeMonthExamplePosts(payload?.examplePosts);
    const view: MonthView = {
      month: payload?.month || monthStr,
      days: mapped,
      trendingTopic: asOptionalString(payload?.trendingTopic),
      trendingName: asOptionalString(payload?.trendingName),
      ...(examplePosts.length ? { examplePosts } : {}),
      ...(monthSecondaries.length ? { secondaryHighlights: monthSecondaries } : {}),
    };
    this.cache.set(cacheKey, view);
    return view;
  }
}
